import Joi from 'joi';
import { TFunction } from 'i18next';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import { newGuidNil } from 'utils/guid';
import { isObjectEqual } from 'utils/object';
import { areNormIdsEqual } from 'models/auditCriteria';

export enum AuditType {
  Internal = 0,
  External = 1,
}

export enum AuditStatus {
  Created = 0,
  Planned = 1,
  Preparing = 2,
  Executing = 3,
  Completed = 4,
  Published = 5,
}

export enum AuditOrigin {
  Scheduled = 0,
  Manual = 1,
}

// One outcome vocabulary for every scope family (controls, processes, risks, assets,
// processing activities, scope documents)
export enum AuditOutcome {
  NotAssessed = 0,
  Conform = 1,
  Observation = 2,
  NonConformityMinor = 3,
  NonConformityMajor = 4,
}

export enum AuditUserRole {
  LeadAuditor = 0,
  Auditor = 1,
  Auditee = 2,
  Observer = 3,
}

// Child tables use the composite key {tenantId, internalAuditId, <entity>Id}; no surrogate id.
export class InternalAuditUser {
  internalAuditId: number;

  userId: string;

  role: AuditUserRole;

  constructor() {
    this.internalAuditId = 0;
    this.userId = newGuidNil();
    this.role = AuditUserRole.Auditor;
  }

  clone(): InternalAuditUser {
    const item = new InternalAuditUser();
    item.internalAuditId = this.internalAuditId;
    item.userId = this.userId;
    item.role = this.role;

    return item;
  }
}

// Matches the Graph availabilityView characters; Unknown = the calendar could not be read
// (guest mailbox, permission problem or a failed Graph call).
export enum AuditAvailabilityStatus {
  Free = 0,
  Tentative = 1,
  Busy = 2,
  Oof = 3,
  WorkingElsewhere = 4,
  Unknown = 5,
}

// tentative, busy and out of office count as a conflict; working elsewhere does not - the
// meeting is online, so the participant's location is irrelevant
export const isAvailabilityConflict = (status: AuditAvailabilityStatus): boolean => {
  return (
    status === AuditAvailabilityStatus.Tentative ||
    status === AuditAvailabilityStatus.Busy ||
    status === AuditAvailabilityStatus.Oof
  );
};

// Server-computed, read-only: no clone/isEqual because availability never takes part in the
// dirty check of the audit.
export class AuditMeetingSlot {
  start?: Date;

  end?: Date;
}

export class AuditParticipantAvailability {
  userId: string;

  name?: string;

  role: AuditUserRole;

  required: boolean;

  status: AuditAvailabilityStatus;

  conflicts: AuditMeetingSlot[];

  constructor() {
    this.userId = newGuidNil();
    this.role = AuditUserRole.Auditor;
    this.required = true;
    this.status = AuditAvailabilityStatus.Unknown;
    this.conflicts = [];
  }
}

export class AuditMeetingAvailability {
  start?: Date;

  end?: Date;

  participants: AuditParticipantAvailability[];

  suggestedSlots: AuditMeetingSlot[];

  hasConflicts: boolean;

  constructor() {
    this.participants = [];
    this.suggestedSlots = [];
    this.hasConflicts = false;
  }
}

// Result of saving an audit as the setup of its series. pendingAuditId is the draft occurrence
// that is still on the OLD setup: re-seeding it throws away prepared work, so the user decides.
export class InternalAuditSaveAsTemplateResult {
  internalAuditTemplateId: number;

  pendingAuditId?: number;

  pendingAuditName?: string;

  constructor() {
    this.internalAuditTemplateId = 0;
  }
}

// One task that is the execution work of the audit. Findings are not in here: they are derived from
// the follow-up relations of these tasks.
export class InternalAuditTask {
  internalAuditId: number;

  taskId: number;

  constructor() {
    this.internalAuditId = 0;
    this.taskId = 0;
  }

  clone(): InternalAuditTask {
    const item = new InternalAuditTask();
    item.internalAuditId = this.internalAuditId;
    item.taskId = this.taskId;

    return item;
  }
}

export class InternalAuditLink {
  internalAuditId: number;

  linkId: number;

  constructor() {
    this.internalAuditId = 0;
    this.linkId = 0;
  }

  clone(): InternalAuditLink {
    const item = new InternalAuditLink();
    item.internalAuditId = this.internalAuditId;
    item.linkId = this.linkId;

    return item;
  }
}

export default class InternalAudit {
  tenantId: string;

  internalAuditId: number;

  internalAuditTemplateId: number;

  name: string;

  status: AuditStatus;

  origin: AuditOrigin;

  startDate?: Date;

  endDate?: Date;

  conclusion?: string;

  // The planned moment of the Teams meeting: user data, edited in the timeline callout, and NOT
  // the audit period above - an audit runs for days, the meeting happens at a moment inside it
  meetingStart?: Date;

  meetingEnd?: Date;

  // Teams meeting state: server-owned (written by the timeline actions), so these are
  // deliberately NOT part of isEqual - they must never dirty the Save button
  meetingEventId?: string;

  meetingOrganizerUserId?: string;

  meetingJoinUrl?: string;

  meetingCreated?: Date;

  meetingUpdated?: Date;

  // the meeting in Outlook no longer matches the plan (moment, name or team changed since the
  // last sync): the timeline offers a manual update for it
  meetingIsStale: boolean;

  invitationSent?: Date;

  finalInvitationSent?: Date;

  authSchemaId?: number;

  // the library document the report lives in: generated from the audit, then edited like any other
  // document, and rendered to the PDF that is attached when the audit is published
  reportLinkId?: number;

  // the report PDF frozen at publish: the immutable record the reporting tab shows (and download
  // and send serve) once the audit is published
  reportPdfLinkId?: number;

  // the status the stored report was written from: below Completed the document carries an INTERIM
  // title, which is how the reporting tab knows the report no longer describes the audit
  reportGeneratedAtStatus?: number;

  reportGeneratedOn?: Date;

  commentTrailId: number;

  auditTrailId: number;

  modified?: Date;

  users: InternalAuditUser[];

  tasks: InternalAuditTask[];

  links: InternalAuditLink[];

  // The standards (tenant norm ids) this occurrence audits against: they decide which criteria its
  // execution tasks ask and under which standards the AI audits run. Editable while the status is at
  // most Preparing. Undefined = unknown (not loaded): never coerced to [], and not sent back.
  normIds: number[] | undefined;

  constructor() {
    this.tenantId = newGuidNil();
    this.internalAuditId = 0;
    this.internalAuditTemplateId = 0;
    this.name = '';
    this.status = AuditStatus.Created;
    this.origin = AuditOrigin.Scheduled;
    this.commentTrailId = 0;
    this.auditTrailId = 0;
    this.meetingIsStale = false;
    this.users = [];
    this.tasks = [];
    this.links = [];
    this.normIds = undefined;
  }

  isEqual(item: InternalAudit): boolean {
    if (item.internalAuditId !== this.internalAuditId) return false;
    if (item.name !== this.name) return false;
    if (item.status !== this.status) return false;
    if (item.startDate?.getTime() !== this.startDate?.getTime()) return false;
    if (item.endDate?.getTime() !== this.endDate?.getTime()) return false;
    if (item.meetingStart?.getTime() !== this.meetingStart?.getTime()) return false;
    if (item.meetingEnd?.getTime() !== this.meetingEnd?.getTime()) return false;
    if (item.conclusion !== this.conclusion) return false;
    if (
      !isObjectEqual(
        item.users.map((u) => u.userId + ':' + u.role),
        this.users.map((u) => u.userId + ':' + u.role),
      )
    )
      return false;
    if (!areNormIdsEqual(item.normIds, this.normIds)) return false;

    return true;
  }

  clone(): InternalAudit {
    const item = new InternalAudit();
    item.tenantId = this.tenantId;
    item.internalAuditId = this.internalAuditId;
    item.internalAuditTemplateId = this.internalAuditTemplateId;
    item.name = this.name;
    item.status = this.status;
    item.origin = this.origin;
    item.startDate = this.startDate ? new Date(this.startDate) : undefined;
    item.endDate = this.endDate ? new Date(this.endDate) : undefined;
    item.conclusion = this.conclusion;
    item.meetingStart = this.meetingStart ? new Date(this.meetingStart) : undefined;
    item.meetingEnd = this.meetingEnd ? new Date(this.meetingEnd) : undefined;
    item.meetingEventId = this.meetingEventId;
    item.meetingOrganizerUserId = this.meetingOrganizerUserId;
    item.meetingJoinUrl = this.meetingJoinUrl;
    item.meetingCreated = this.meetingCreated ? new Date(this.meetingCreated) : undefined;
    item.meetingUpdated = this.meetingUpdated ? new Date(this.meetingUpdated) : undefined;
    item.meetingIsStale = this.meetingIsStale;
    item.invitationSent = this.invitationSent ? new Date(this.invitationSent) : undefined;
    item.finalInvitationSent = this.finalInvitationSent ? new Date(this.finalInvitationSent) : undefined;
    item.authSchemaId = this.authSchemaId;
    item.reportLinkId = this.reportLinkId;
    item.reportPdfLinkId = this.reportPdfLinkId;
    item.reportGeneratedAtStatus = this.reportGeneratedAtStatus;
    item.reportGeneratedOn = this.reportGeneratedOn ? new Date(this.reportGeneratedOn) : undefined;
    item.commentTrailId = this.commentTrailId;
    item.auditTrailId = this.auditTrailId;
    item.modified = this.modified ? new Date(this.modified) : undefined;
    item.users = this.users.map((u) => u.clone());
    item.tasks = this.tasks.map((t) => t.clone());
    item.links = this.links.map((l) => l.clone());
    item.normIds = this.normIds ? [...this.normIds] : undefined;

    return item;
  }

  validate(localizedFields: Record<string, string>): Joi.ValidationResult {
    const schema: Joi.ObjectSchema = Joi.object({
      name: Joi.string().max(512).required().label(localizedFields['name']),
    }).prefs(getLocalizedMessageOptions());

    return schema.validate({ name: this.name }, { abortEarly: false });
  }

  // The order the audit walks its statuses in, one step at a time (the server refuses a jump with
  // AUDIT0017). The timeline and the readiness check both derive "the next step" from this list.
  static readonly statusFlow: AuditStatus[] = [
    AuditStatus.Created,
    AuditStatus.Planned,
    AuditStatus.Preparing,
    AuditStatus.Executing,
    AuditStatus.Completed,
    AuditStatus.Published,
  ];

  static getPreviousStatus = (status: AuditStatus): AuditStatus | undefined => {
    const index = InternalAudit.statusFlow.indexOf(status);

    return index > 0 ? InternalAudit.statusFlow[index - 1] : undefined;
  };

  // undefined for Published: the terminal step has nothing to move on to
  static getNextStatus = (status: AuditStatus): AuditStatus | undefined => {
    const index = InternalAudit.statusFlow.indexOf(status);

    return index >= 0 && index < InternalAudit.statusFlow.length - 1 ? InternalAudit.statusFlow[index + 1] : undefined;
  };

  static getStatusText = (status: AuditStatus, t: TFunction<string[]>): string => {
    switch (status) {
      case AuditStatus.Created:
        return t('audit:Status.Created');
      case AuditStatus.Planned:
        return t('audit:Status.Planned');
      case AuditStatus.Preparing:
        return t('audit:Status.Preparing');
      case AuditStatus.Executing:
        return t('audit:Status.Executing');
      case AuditStatus.Completed:
        return t('audit:Status.Completed');
      case AuditStatus.Published:
        return t('audit:Status.Published');
      default:
        return '';
    }
  };

  static getTypeText = (type: AuditType, t: TFunction<string[]>): string => {
    switch (type) {
      case AuditType.External:
        return t('audit:Type.External');
      default:
        return t('audit:Type.Internal');
    }
  };

  static getOutcomeText = (outcome: AuditOutcome, t: TFunction<string[]>): string => {
    switch (outcome) {
      case AuditOutcome.Conform:
        return t('audit:Outcome.Conform');
      case AuditOutcome.Observation:
        return t('audit:Outcome.Observation');
      case AuditOutcome.NonConformityMinor:
        return t('audit:Outcome.NonConformityMinor');
      case AuditOutcome.NonConformityMajor:
        return t('audit:Outcome.NonConformityMajor');
      default:
        return t('audit:Outcome.NotAssessed');
    }
  };

  static getRoleText = (role: AuditUserRole, t: TFunction<string[]>): string => {
    switch (role) {
      case AuditUserRole.LeadAuditor:
        return t('audit:Role.LeadAuditor');
      case AuditUserRole.Auditee:
        return t('audit:Role.Auditee');
      case AuditUserRole.Observer:
        return t('audit:Role.Observer');
      default:
        return t('audit:Role.Auditor');
    }
  };

  // Who may be handed an execution task: anyone on the team except an observer. The server states the
  // same rule (InternalAuditTeamPermissionRules.CanHoldAuditWork) and refuses the rest with AUDIT0008.
  // An auditor or lead auditor holding their own work is what a one-person audit is: a person holds
  // ONE role per audit, so a solo auditor can never also be listed as auditee.
  static canHoldAuditWork = (role: AuditUserRole): boolean => role !== AuditUserRole.Observer;
}
